<template>
  <label class="cn-input">
    <span
      v-if="label"
      class="cn-input__label"
      :class="{ 'cn-input__label--disabled': disabled }"
    >
      {{ label }}
    </span>
    <UiIcon v-if="icon" :name="icon" class="cn-input__icon" />
    <input
      ref="input"
      v-model="valueLocal"
      class="cn-input__input"
      :class="[
        { 'cn-input__input--with-icon': icon },
        error && 'cn-input__input--error',
      ]"
      :placeholder="placeholder"
      :type="type"
      :readonly="readonly"
    />
    <p v-if="error" class="cn-input__error">
      {{ error }}
    </p>
  </label>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  label?: string;
  modelValue: string | number;
  icon?: string;
  placeholder?: string;
  type?: string;
  readonly?: boolean;
  disabled?: boolean;
  error?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', data: string | number): void;
}>();

const valueLocal = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});

const input = ref();
</script>

<style lang="scss">
.cn-input {
  --ab-canyon-input-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-input-error-color: var(--ab-canyon-color-danger);
  --ab-canyon-input-text-color: var(--ab-canyon-color-dark);
  --ab-canyon-input-placeholder-color: var(--ab-canyon-color-neutral-dark);
  --ab-canyon-input-border-color: var(--ab-canyon-color-neutral);
  --ab-canyon-input-border-color-focus: var(--ab-canyon-color-neutral-dark);
  --ab-canyon-input-border-color-readonly: var(--ab-canyon-color-neutral);
  --ab-canyon-input-outline-color-focus: var(--ab-canyon-color-neutral-light);
  --ab-canyon-input-icon-color: var(--ab-canyon-color-neutral-dark);

  position: relative;
  display: block;
  width: 100%;
  max-width: calc(42 * var(--size-base));
  &__input {
    background-color: var(--ab-canyon-input-color-bg);
    width: 100%;
    padding: 0.625rem var(--size-lg);
    color: var(--ab-canyon-input-text-color);
    border-color: var(--ab-canyon-input-border-color);
    transition-property: color, border-color, box-shadow;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;

    &::placeholder {
      color: var(--ab-canyon-input-placeholder-color);
    }

    &:focus {
      border-color: var(--ab-canyon-input-border-color-focus);
      outline: none;
      outline-offset: 0;
      outline-color: var(--ab-canyon-input-outline-color-focus);
      box-shadow: 0px 0px 4px -1px
        color-mix(
          in srgb,
          var(--ab-canyon-input-border-color-focus),
          transparent 20%
        );
    }

    &:read-only {
      cursor: not-allowed;
      border-color: var(--ab-canyon-input-border-color-readonly);
    }

    @media (min-width: 640px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    &--with-icon {
      padding-left: var(--size-4xl);
    }
    &--error {
      border-color: var(--ab-canyon-input-error-color);
    }
  }
  &__error {
    padding: calc(var(--size-base) / 4) 0;
    color: var(--ab-canyon-input-error-color);
  }
  &__label {
    position: absolute;
    top: -0.375rem;
    left: 0.875rem;
    z-index: 10;
    white-space: nowrap;
    padding: 0 0.25rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--ab-canyon-input-text-color);
    background-color: var(--ab-canyon-input-color-bg);
    &--disabled {
      color: var(--ab-canyon-color-neutral);
    }
  }

  &__icon {
    position: absolute;
    left: var(--size-xs);
    top: 50%;
    transform: translateY(-50%);
    width: var(--size-lg);
    height: var(--size-lg);
    color: var(--ab-canyon-input-icon-color);
  }
}
</style>
